import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { saveAuthData, getAuthHeader } from '../../services/authService';
import { getApiUrl } from '../../utils/config';
import { useAuth } from '../../context/AuthContext';

interface StravaResponse {
  result: string;
  message: string;
  data?: any;
}

const ExchangeTokenPage: React.FC = () => {
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState<string>('Đang xử lý xác thực Strava...');
  const navigate = useNavigate();
  const location = useLocation();
  const hasCalledApiRef = useRef<boolean>(false);
  const { setIsLoggedIn, isAuthenticated } = useAuth();

  useEffect(() => {
    const processCallback = async () => {
      // Kiểm tra xem API đã được gọi chưa
      if (hasCalledApiRef.current) {
        console.log('API đã được gọi rồi, bỏ qua lần gọi này');
        return;
      }
      
      try {
        // Đánh dấu là API đã được gọi
        hasCalledApiRef.current = true;
        
        // Lấy code từ URL
        const queryParams = new URLSearchParams(location.search);
        const code = queryParams.get('code');
        const scope = queryParams.get('scope');

        if (!code) {
          setStatus('error');
          setMessage('Không tìm thấy mã xác thực từ Strava');
          return;
        }

        console.log('Đã nhận code từ Strava:', code);
        console.log('Scope được cấp quyền:', scope);

        // Kiểm tra xem người dùng đã đăng nhập chưa
        const authHeader = getAuthHeader();
        
        // Lấy thông tin luồng xác thực
        const authFlow = localStorage.getItem('strava_auth_flow');
        console.log('Luồng xác thực Strava:', authFlow);
        
        // Nếu đến từ trang login hoặc chưa đăng nhập, sử dụng API login
        if (authFlow === 'login' || (!isAuthenticated || !authHeader)) {
          console.log('Sử dụng API login với Strava');
          
          // Gọi API đăng nhập bằng Strava
          const response = await fetch(`${getApiUrl()}/api/auth/strava/login?code=${code}`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json'
            }
          });

          const data: StravaResponse = await response.json();

          if (!response.ok || data.result !== 'SUCCESS') {
            throw new Error(data.message || 'Có lỗi xảy ra khi đăng nhập với Strava');
          }

          // Nếu đăng nhập thành công, lưu token và thông tin người dùng
          if (data.data) {
            // Lưu token vào localStorage
            saveAuthData({
              token: data.data.token,
              type: data.data.tokenType,
              id: data.data.user.id,
              username: data.data.user.username
            });
            
            // Lưu thông tin user
            localStorage.setItem('user', JSON.stringify(data.data.user));
            
            // Lưu thông tin Strava
            localStorage.setItem('stravaConnected', 'true');
            if (data.data.user.stravaAthleteId) {
              localStorage.setItem('stravaAthleteId', data.data.user.stravaAthleteId.toString());
            }
            
            // Cập nhật context đăng nhập
            setIsLoggedIn(true);
            
            console.log('Đăng nhập bằng Strava thành công:', data.data.user.username);
          }

          // Cập nhật trạng thái thành công
          setStatus('success');
          setMessage('Đăng nhập bằng Strava thành công');

          // Xác định trang chuyển hướng dựa trên role của người dùng
          const userRole = data.data?.user?.role || '';
          const redirectPath = userRole.includes('ADMIN') ? '/dashboard' : '/teams';

          // Chuyển hướng về trang dashboard hoặc trang chủ sau 2 giây
          setTimeout(() => {
            navigate(redirectPath, { 
              state: { stravaConnected: true }
            });
          }, 2000);
        } 
        // Nếu đã đăng nhập và đến từ luồng connect, sử dụng API connect
        else if (isAuthenticated && authHeader && authFlow === 'connect') {
          console.log('Người dùng đã đăng nhập, sử dụng API connect');
          
          // Gọi API kết nối tài khoản Strava với tài khoản hiện tại
          const response = await fetch(`${getApiUrl()}/api/auth/strava/connect?code=${code}`, {
            method: 'GET',
            headers: {
              'Authorization': authHeader,
              'Content-Type': 'application/json'
            }
          });

          const data: StravaResponse = await response.json();

          if (!response.ok || data.result !== 'SUCCESS') {
            throw new Error(data.message || 'Có lỗi xảy ra khi kết nối với Strava');
          }

          // Cập nhật trạng thái thành công
          setStatus('success');
          setMessage('Kết nối với Strava thành công!');
          
          // Lưu thông tin Strava
          localStorage.setItem('stravaConnected', 'true');
          if (data.data?.athleteId) {
            localStorage.setItem('stravaAthleteId', data.data.athleteId.toString());
          }
          
          // Chuyển hướng về trang hồ sơ
          setTimeout(() => {
            navigate('/profile', { 
              state: { stravaConnected: true }
            });
          }, 2000);
        }
        // Trường hợp mặc định nếu không rõ luồng
        else {
          console.log('Không rõ luồng xác thực, mặc định sử dụng API login');
          
          // Sử dụng API login 
          const response = await fetch(`${getApiUrl()}/api/auth/strava/login?code=${code}`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json'
            }
          });

          const data: StravaResponse = await response.json();

          if (!response.ok || data.result !== 'SUCCESS') {
            throw new Error(data.message || 'Có lỗi xảy ra khi đăng nhập với Strava');
          }

          // Xử lý response tương tự như ở trên
          if (data.data) {
            saveAuthData({
              token: data.data.token,
              type: data.data.tokenType,
              id: data.data.user.id,
              username: data.data.user.username
            });
            
            localStorage.setItem('user', JSON.stringify(data.data.user));
            localStorage.setItem('stravaConnected', 'true');
            if (data.data.user.stravaAthleteId) {
              localStorage.setItem('stravaAthleteId', data.data.user.stravaAthleteId.toString());
            }
            
            setIsLoggedIn(true);
          }

          setStatus('success');
          setMessage('Đăng nhập bằng Strava thành công');

          const userRole = data.data?.user?.role || '';
          const redirectPath = userRole.includes('ADMIN') ? '/dashboard' : '/teams';
          
          setTimeout(() => {
            navigate(redirectPath, { 
              state: { stravaConnected: true }
            });
          }, 2000);
        }
        
        // Xóa trạng thái luồng xác thực sau khi xử lý xong
        localStorage.removeItem('strava_auth_flow');
        
      } catch (error: any) {
        console.error('Lỗi khi xử lý Strava callback:', error);
        setStatus('error');
        setMessage(error.message || 'Đã xảy ra lỗi khi xử lý với Strava');
        
        // Xóa trạng thái luồng xác thực khi có lỗi
        localStorage.removeItem('strava_auth_flow');
        
        // Chuyển hướng về trang đăng nhập sau 3 giây
        setTimeout(() => {
          navigate('/login', { 
            state: { stravaError: true, errorMessage: error.message }
          });
        }, 3000);
      }
    };

    processCallback();
  }, [location, navigate, setIsLoggedIn, isAuthenticated]);

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      backgroundColor: '#f5f5f5'
    }}>
      <div style={{
        background: 'white',
        padding: '2rem',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        textAlign: 'center',
        maxWidth: '400px',
        width: '100%'
      }}>
        {status === 'loading' && (
          <div>
            <h2>Đang xử lý...</h2>
            <p>{message}</p>
            <div style={{
              width: '40px',
              height: '40px',
              margin: '20px auto',
              border: '4px solid #f3f3f3',
              borderTop: '4px solid #fc4c02',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }} />
            <style>{`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}</style>
          </div>
        )}

        {status === 'success' && (
          <div style={{ color: '#2e7d32' }}>
            <h2 style={{ 
              fontSize: '24px', 
              fontWeight: 'bold', 
              marginBottom: '16px' 
            }}>Xác thực thành công!</h2>
            <div style={{
              backgroundColor: 'rgba(76,175,80,0.1)',
              border: '1px solid rgba(76,175,80,0.3)',
              borderRadius: '4px',
              padding: '15px',
              marginBottom: '15px'
            }}>
              <p style={{ marginBottom: '8px' }}>{message}</p>
              <p>Bạn sẽ được chuyển hướng trong giây lát...</p>
            </div>
          </div>
        )}

        {status === 'error' && (
          <div style={{ color: '#e74c3c' }}>
            <h2>Lỗi xác thực</h2>
            <p>{message}</p>
            <button 
              onClick={() => navigate('/login')}
              style={{
                backgroundColor: '#fc4c02',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '4px',
                cursor: 'pointer',
                marginTop: '20px',
                fontWeight: 'bold'
              }}
            >
              Quay lại đăng nhập
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExchangeTokenPage; 